import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import {
  listCloudDocuments,
  uploadCloudDocument,
  deleteCloudDocument,
  getCloudUsage,
} from '../lib/cloudApi';
import { renderPdfThumbnail } from '../lib/pdfThumbnail';

/**
 * useCloud — document list + usage state for /cloud and Account pages.
 */
export function useCloud({ autoLoad = true } = {}) {
  const { account } = useAuth();
  const hasCloud = (account?.capabilities?.cloud_bytes || 0) > 0;
  const [documents, setDocuments] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [docs, u] = await Promise.all([
        hasCloud ? listCloudDocuments() : Promise.resolve({ documents: [] }),
        getCloudUsage().catch(() => null),
      ]);
      setDocuments(docs.documents || []);
      if (u) setUsage(u);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [hasCloud]);

  useEffect(() => {
    if (autoLoad && account) refresh();
  }, [autoLoad, account, refresh]);

  const upload = useCallback(async (file, meta) => {
    const thumbnail = await renderPdfThumbnail(file).catch(() => null);
    const data = await uploadCloudDocument(file, { ...(meta || {}), thumbnail });
    await refresh();
    return data;
  }, [refresh]);

  const remove = useCallback(async (docId) => {
    await deleteCloudDocument(docId);
    setDocuments((prev) => prev.filter((d) => d.id !== docId));
    getCloudUsage().then(setUsage).catch(() => {});
  }, []);

  return {
    hasCloud,
    documents,
    usage,
    loading,
    error,
    refresh,
    upload,
    remove,
  };
}
